import React, { useEffect, useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"
import { ArrowRightIcon, BellRingingIcon, HandbagIcon, XIcon } from "@phosphor-icons/react"
import { Dialog, DialogPanel } from "@headlessui/react"
import { navigate } from "gatsby"

import Tag from "@components/common/Tag"
import StarRating from "@components/common/StarRating"
import SizePanelDynamic from "@components/Products/common/SizePanelDynamic"
import LinkButton from "@components/common/LinkButton"
import NotifyMeModal from '@components/modal/NotifyMeModal'

import { StoreContext } from "@context/StoreContext"
import QuickBuyContext from "@context/QuickBuyContext"

import { productFormattedPrice } from "@utils"
import { sendAddToCartGAEvent } from "@utils/analytics/analytics"
import { GA_EVENTS } from "@utils/analytics/constants"

const QuickBuyModal = ({ data, isOpen = false, onClose = () => {}, isAddToCart}) => {
    const [size, setSize] = useState('');
    const { addVariantToCart, buyNow, loading, checkout } = React.useContext(StoreContext)
    const { quickBuyData, setQuickBuyData, addToBagClicked, setAddToBagClicked } = React.useContext(QuickBuyContext)
    const productNameVariant = data?.title?.split(" / ")
    const [sizes, setSizes] = useState([]);
    const [openNotifyModal, setOpenNotifyModal] = useState(false)

    useEffect(() => {
        if(!quickBuyData && !addToBagClicked)
            handleClose()
    }, [quickBuyData, addToBagClicked])

    const handleClose = () => {
        onClose()
    }

    const handleOpenNotifyMe = () => {
        setOpenNotifyModal(true)
        //onClose()
    }

    const handleCheckout = (e) => {
        e.preventDefault();
        buyNow(size?.id, 1)
    }

    const handleAddToBag = async (e) => {
        e.preventDefault();
		const cartData = await addVariantToCart(size?.id, 1);
		if (cartData) sendAddToCartGAEvent(GA_EVENTS.ADD_TO_CART, size, cartData)
        handleClose();
    }

    const handleNavigate = (e) => {
        setQuickBuyData(null)
        setAddToBagClicked(null)
        navigate(`/products/${data?.handle}`)
    }

    const openState = isOpen || false;
    
    const isDiscounted = data?.priceRangeV2?.minVariantPrice?.amount !== data?.variants?.[0]?.compareAtPrice ? true : false;
    const badgeItems = data?.tags?.filter(item => item.startsWith("badge_"));
    const ratings = data?.metafields?.find(item => item.key === "avg_rating");
    const numberOfReviews = data?.metafields?.find(item => item.key === "num_reviews");

    const originalImage = React.useMemo(() => {
        const imageSrc = data?.media?.[1]?.preview?.image?.src;

        if (!imageSrc) {
            //console.warn("Image source is undefined, falling back to placeholder or null.");
            return null; // Or return a placeholder image object here if needed
        }


        const image = {
            originalSrc: data?.media?.[0]?.preview?.image?.src,
            width: data?.media?.[0]?.preview?.image?.gatsbyImageData?.width,
            height: data?.media?.[0]?.preview?.image?.gatsbyImageData?.height,
        }
    
        return getShopifyImage({
            image: image,
            layout: "constrained",
            crop: "contain",
            width: 104,
            height: 155,
        });
    }, [data?.media?.[0]?.preview?.image?.src]);

    useEffect(() => {
        const fetchInventory = async () => {
            const response = await fetch("https://" + process.env.GATSBY_MYSHOPIFY_URL + "/api/2025-01/graphql.json", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
            },
            body: JSON.stringify({
                query: `
                query GetProductInventory($handle: String!) {
                    productByHandle(handle: $handle) {
                    variants(first: 50) {
                        edges {
                        node {
                            title
                            availableForSale
                            quantityAvailable
                            id
                        }
                        }
                    }
                    }
                }
                `,
                variables: { handle: data?.handle },
            }),
            });

            const result = await response.json();
            const sizes = result?.data?.productByHandle?.variants?.edges?.map(variant => variant?.node) || [];

            setSizes(sizes)
        };

        fetchInventory();
    }, [data]);

    return (
        <React.Fragment>
            <Dialog open={openState} onClose={handleClose} className="relative z-[101]">
                <div className="fixed inset-0 w-screen overflow-y-auto p-4 bg-black bg-opacity-60">
                    <div className="flex min-h-full items-center justify-center">
                        <DialogPanel className="relative border bg-white p-6 w-128 overflow-y-auto minimalist-scrollbar gap-6">
                            <XIcon
                                className="absolute top-0 right-0 cursor-pointer text-black m-4"
                                size={32}
                                weight="light"
                                onClick={handleClose}
                            />

                            <section className="flex flex-nowrap gap-x-4">
                                <div onClick={() => handleNavigate()}>
                                    {
                                        originalImage ? <GatsbyImage
                                            className="max-w-[104px] cursor-pointer"
                                            alt={data?.altText || data?.title}
                                            image={originalImage}
                                        /> : null
                                    }
                                    
                                </div>
                                <div className="flex-1">
                                    <h1 className="leading-normal font-normal text-[20px] mr-10 cursor-pointer" 
                                        onClick={() => handleNavigate()}
                                    >
                                        {productNameVariant?.[0]?.replace("FINAL SALE", "")}
                                    </h1>
                                    <h4>{productNameVariant?.[1]?.replace("FINAL SALE", "")}</h4>
                                    <div className="inline-flex gap-x-1 text-xxs">
                                    {
                                        badgeItems?.map((tagName, key) => {
                                            return (
                                                <Tag
                                                    label={tagName?.replace("badge_", "").replace("-", " ")}
                                                    key={key}
                                                    className=" uppercase bg-gray-50 border-2 border-gray-200"
                                                />
                                            )
                                        })
                                    }
                                    </div>
                                    <div className="flex items-center justify-between mt-4 mb-2 border-t border-b border-gray-300 py-2">

                                    <div className="flex gap-x-4 font-medium">
                                        {isDiscounted && <span>
                                                            {
                                                                checkout?.cost?.totalAmount?.currencyCode ?
                                                                    productFormattedPrice(
                                                                        checkout?.cost?.totalAmount?.currencyCode,
                                                                        data?.priceRangeV2?.minVariantPrice?.amount
                                                                    )
                                                                    : null
                                                            }
                                                        </span>}
                                        <span className={`${isDiscounted ? "discounted" : ""}`} >
                                            {
                                                checkout?.cost?.totalAmount?.currencyCode ?
                                                    productFormattedPrice(
                                                        checkout?.cost?.totalAmount?.currencyCode,
                                                        data?.variants[0]?.compareAtPrice
                                                    )
                                                    : null
                                            }
                                        </span>
                                    </div>

                                    <div className="flex gap-x-2">
                                        <StarRating
                                            totalStars={1}
                                            initalRating={1}
                                            readOnly={true}
                                        />
                                        <span>{ratings?.value ? ratings?.value : ""}</span>
                                        <span className="text-[#D1D1D1]">({numberOfReviews?.value ? numberOfReviews?.value : '0' })</span>
                                    </div>
                                    </div>
                                </div>
                            </section>

                            <section className="pb-12">
                                <SizePanelDynamic sizes={sizes} setSize={setSize} />
                            </section>

                            <div className="w-full flex">
                                {
                                    isAddToCart ? 
										size?.availableForSale === false ?
                                            <div className={`w-full`}>
                                                <button className='w-full btn-outline text-primary flex gap-2 justify-center items-center' onClick={handleOpenNotifyMe} /*setOpenNotifyModal(true)}*/>
                                                    <BellRingingIcon size={20} weight='light' /> 
                                                    <span>Notify me when available</span>
                                                </button>
                                            </div>
                                        :
                                        <div className="w-full space-y-4">
                                            <LinkButton
                                                className="w-full flex flex-1 btn-primary justify-center items-center"
                                                label={"Confirm Add to Bag"}
                                                icon={<HandbagIcon size={20} weight="light" />}
                                                iconPosition="start"
                                                onClick={(e) => handleAddToBag(e)}
                                                disabled={loading || !size?.availableForSale}
                                                ariaLabel="Confirm Add to Bag"
                                            />
                                            <LinkButton
                                                className="w-full flex flex-1 btn-outline justify-center items-center"
                                                label={"Checkout Instead"}
                                                icon={<ArrowRightIcon size={20} weight="light" />}
                                                onClick={(e) => handleCheckout(e)}
                                                disabled={loading || !size?.availableForSale}
                                                ariaLabel="Checkout Instead"
                                            />
                                        </div> :
                                        <LinkButton
                                            className="w-full flex flex-1 btn-primary justify-center items-center"
                                            label={"Proceed to Checkout"}
                                            icon={<ArrowRightIcon size={20} weight="light" />}
                                            onClick={(e) => handleCheckout(e)}
                                            disabled={loading || !size?.availableForSale}
                                            ariaLabel="Proceed to Checkout"
                                        />
                                }
                                
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            <NotifyMeModal
                productName={productNameVariant}
                isOpen={openNotifyModal}
                size={size}
                notifySizes={sizes?.filter(item => item?.quantityAvailable <= 0)}
                onClose={() => setOpenNotifyModal(false)}
                product_id={data?.shopifyId}
            />
        </React.Fragment>
    );
}

export default QuickBuyModal;